import React, { useEffect, useRef } from 'react';
import type { User } from '../../../types';
import type { ChangeWorkGroup } from '../../../types/changes';
import { useIncidentFirstContactGroups } from '../hooks/useIncidentFirstContactGroups';
import {
  incidentAllowsAttachments,
  incidentCustomFieldDefaultsForWorkflow,
  type IncidentSendDraft,
  type IncidentWorkflowOption,
} from '../incidentQuickSend';
import IncidentNovoTicketInfoFields from './IncidentNovoTicketInfoFields';
import IncidentTicketFieldsForm from './IncidentTicketFieldsForm';

interface Props {
  group: ChangeWorkGroup;
  draft: IncidentSendDraft;
  workflows: IncidentWorkflowOption[];
  users?: Array<Pick<User, 'id' | 'name' | 'email'>>;
  onPatch: (patch: Partial<IncidentSendDraft>) => void;
}

const IncidentSendPackageForm: React.FC<Props> = ({
  group,
  draft,
  workflows,
  users = [],
  onPatch,
}) => {
  const onPatchRef = useRef(onPatch);
  onPatchRef.current = onPatch;
  const { groups, loading } = useIncidentFirstContactGroups(draft.workflow_id);

  useEffect(() => {
    if (!draft.workflow_id || loading) return;
    if (!groups.length) return;
    if (draft.incidents_group_id && groups.some((item) => item.id === draft.incidents_group_id)) {
      return;
    }
    const nextGroup = groups.length === 1 ? groups[0].id : undefined;
    if (draft.incidents_group_id === nextGroup) return;
    if (nextGroup !== undefined) {
      onPatchRef.current({ incidents_group_id: nextGroup });
    }
  }, [draft.incidents_group_id, draft.workflow_id, groups, loading]);

  const forwardQueueName = groups.find((item) => item.id === draft.incidents_group_id)?.name;

  return (
    <IncidentTicketFieldsForm
      workflowId={draft.workflow_id}
      incidentsGroupId={draft.incidents_group_id}
      forwardQueueName={forwardQueueName}
      values={draft.customFieldValues}
      files={draft.customFieldFiles}
      ariaPrefix={`Solicitação de ${group.name}`}
      users={users}
      onValuesChange={(customFieldValues) => onPatch({ customFieldValues })}
      onFilesChange={(customFieldFiles) => onPatch({ customFieldFiles })}
      onFieldsChange={(customFields) => onPatch({ customFields })}
      initialValues={draft.customFieldValues}
      infoTab={
        <IncidentNovoTicketInfoFields
          name={group.name || ''}
          workflowId={draft.workflow_id}
          workflows={workflows}
          onWorkflowChange={(workflowId) =>
            onPatch({
              workflow_id: workflowId,
              incidents_group_id: undefined,
              customFieldValues: incidentCustomFieldDefaultsForWorkflow(group, workflowId),
              customFieldFiles: {},
            })
          }
          groupId={draft.incidents_group_id}
          groups={groups}
          groupsLoading={loading}
          onGroupChange={(incidents_group_id) =>
            onPatch({
              incidents_group_id,
              customFieldValues: {},
              customFieldFiles: {},
              customFields: [],
            })
          }
          urgency={draft.urgency}
          onUrgencyChange={(urgency) => onPatch({ urgency })}
          title={draft.title}
          onTitleChange={(title) => onPatch({ title })}
          description={draft.comments}
          onDescriptionChange={(comments) => onPatch({ comments })}
          allowAttachments={incidentAllowsAttachments(group)}
          onAttachmentsChange={(files) => onPatch({ files })}
        />
      }
    />
  );
};

export default IncidentSendPackageForm;
